import { useEffect, useRef } from 'react';
import Lenis from 'lenis';

/**
 * Scroll con inercia para una página.
 *
 * Sin esto la página sigue a la rueda uno a uno: girás un clic y salta un
 * escalón. Con esto la rueda marca un destino y la página se desliza hasta ahí
 * y frena sola, como si tuviera peso.
 *
 * Va por página y no globalmente a propósito. El panel y el onboarding tienen
 * once contenedores con scroll propio —la grilla de horarios, la tarjeta del
 * paso, el simulador del chat— y una librería que toma el control del scroll
 * de la ventana suele dejarlos trabados.
 *
 * Acá decía que la landing y la página pública no tenían ninguno "así que ahí
 * es seguro". Era cierto para lo que se ve al cargar y falso para los diálogos:
 * la página pública abre el de seña y el de lista de espera, los dos con scroll
 * propio, y los dos quedaron trabados exactamente como decía el párrafo de
 * arriba. Lenis escucha la rueda sobre toda la ventana con `preventDefault`, y
 * la única forma de que un contenedor de adentro vuelva a scrollear es marcarlo
 * con `data-lenis-prevent`: si lo encuentra en el camino del evento, se
 * desentiende. Un contenedor con scroll nuevo en estas dos páginas lo necesita.
 *
 * Devuelve la instancia para quien necesite mover el scroll a mano (el menú de
 * la landing, que scrollea a una sección). Usar `window.scrollTo` mientras esto
 * corre pelea con la inercia: el navegador y la librería mueven la misma
 * página al mismo tiempo y el resultado tiembla.
 */
export default function useScrollSuave({ activo = true } = {}) {
  const instancia = useRef(null);

  useEffect(() => {
    if (!activo) return undefined;

    // Quien pide menos movimiento espera que la página siga al dedo o a la
    // rueda sin intermediarios. La inercia es justamente lo contrario.
    const pideQuieto = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (pideQuieto.matches) return undefined;

    const lenis = new Lenis({
      // Cuánto tarda en alcanzar el destino. Más alto se siente pesado y da
      // sensación de retraso al clickear algo mientras la página se mueve.
      duration: 1.1,
      // Sale rápido y frena largo: es lo que da la sensación de peso. Es la
      // curva que recomienda la librería, exponencial invertida.
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      // En teléfono NO. El scroll táctil del sistema ya tiene su propia
      // inercia, afinada por el fabricante; encimarle otra se siente resbaloso
      // y descoordinado del dedo.
      smoothWheel: true,
      syncTouch: false,
    });

    instancia.current = lenis;

    let cuadro;
    const latir = (tiempo) => {
      lenis.raf(tiempo);
      cuadro = requestAnimationFrame(latir);
    };
    cuadro = requestAnimationFrame(latir);

    return () => {
      cancelAnimationFrame(cuadro);
      lenis.destroy();
      instancia.current = null;
    };
  }, [activo]);

  return instancia;
}
